import { getTintedTagStyle } from '../utils/tagColors.js';
/**
 * Projects View Module
 *
 * Pure computation functions for projects view logic.
 * These functions handle project statistics, sorting, and filtering.
 *
 * @module views/projectsView
 */

import { PRIORITY_ORDER, STATUS_ORDER } from '../config/constants.js';
import { generateKanbanDueChipHTML } from '../components/taskCard.js';

const DAY_MS = 24 * 60 * 60 * 1000;

// "YYYY-MM-DD" as a local-midnight Date (Date('YYYY-MM-DD') would be UTC)
function parseLocalDate(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/**
 * Two-letter monogram for a project: first letter of the first two words
 * ("D9 Academy" → "DA", "Tamir #1" → "T1"), or the first two letters.
 * @param {string} name - Project name
 * @returns {string} Up to two uppercase characters
 */
export function getProjectInitials(name) {
    const words = String(name || '').split(/\s+/)
        .map(w => w.replace(/[^\p{L}\p{N}]/gu, ''))
        .filter(Boolean);
    if (words.length === 0) return '?';
    const initials = words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
    return initials.toUpperCase();
}

/**
 * Deadline state of a project's end date relative to today.
 * @param {Object} project - Project object
 * @param {boolean} isCompleted - All tasks done (no deadline pressure)
 * @param {Date} today - Reference date
 * @returns {{kind: 'overdue'|'today'|'soon', days: number}|null}
 */
export function getProjectDeadlineInfo(project, isCompleted, today = new Date()) {
    const end = parseLocalDate(project.endDate);
    if (!end || isCompleted) return null;
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const days = Math.round((end - start) / DAY_MS);
    if (days < 0) return { kind: 'overdue', days: -days };
    if (days === 0) return { kind: 'today', days: 0 };
    if (days <= 30) return { kind: 'soon', days };
    return null;
}

/**
 * Calculate task statistics for a project
 * @param {Array} tasks - Array of all task objects
 * @param {number} projectId - Project ID to calculate stats for
 * @returns {Object} Task statistics by status
 */
export function calculateProjectTaskStats(tasks, projectId) {
    // Same rule as getProjectCompletionPercent (app.js) so card % and the Progress filter agree
    const projectTasks = tasks.filter(t => t.projectId === projectId && !t.isTemplate && !t.archivedAt);
    
    const stats = {
        total: projectTasks.length,
        backlog: 0,
        todo: 0,
        progress: 0,
        review: 0,
        done: 0
    };
    
    projectTasks.forEach(task => {
        if (stats.hasOwnProperty(task.status)) {
            stats[task.status]++;
        }
    });
    
    return stats;
}

/**
 * Calculate completion percentage for a project
 * @param {Object} stats - Task statistics from calculateProjectTaskStats
 * @returns {number} Completion percentage (0-100)
 */
export function calculateCompletionPercentage(stats) {
    if (stats.total === 0) return 0;
    return Math.round((stats.done / stats.total) * 100);
}

/**
 * Determine project status based on dates and task completion
 * @param {Object} project - Project object
 * @param {Object} stats - Task statistics from calculateProjectTaskStats
 * @param {Date} today - Today's date
 * @returns {Object} Project status info
 */
export function determineProjectStatus(project, stats, today = new Date()) {
    const todayStr = today.toISOString().split('T')[0];

    // Archive trumps every other status. An archived project is never "active",
    // "completed", etc. — it is simply archived until restored.
    if (project.archivedAt) {
        return {
            status: 'archived',
            label: 'Archived',
            class: 'status-archived'
        };
    }

    // Check if all tasks are done
    if (stats.total > 0 && stats.done === stats.total) {
        return {
            status: 'completed',
            label: 'Completed',
            class: 'status-completed'
        };
    }
    
    // Check if project has ended (past end date)
    if (project.endDate && project.endDate < todayStr) {
        return {
            status: 'ended',
            label: 'Ended',
            class: 'status-ended'
        };
    }
    
    // Check if project hasn't started yet
    if (project.startDate && project.startDate > todayStr) {
        return {
            status: 'upcoming',
            label: 'Upcoming',
            class: 'status-upcoming'
        };
    }
    
    // Check if project is in progress (has tasks in progress or review)
    if (stats.progress > 0 || stats.review > 0) {
        return {
            status: 'active',
            label: 'Active',
            class: 'status-active'
        };
    }
    
    // Default: project exists but no active work
    return {
        status: 'idle',
        label: 'Idle',
        class: 'status-idle'
    };
}

/**
 * Sort projects by a specific field
 * @param {Array} projects - Array of project objects
 * @param {string} sortBy - Field to sort by ('name', 'startDate', 'endDate', 'progress')
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @param {Array} tasks - Array of all tasks (needed for progress sorting)
 * @returns {Array} Sorted projects array
 */
export function sortProjects(projects, sortBy, direction = 'asc', tasks = []) {
    return [...projects].sort((a, b) => {
        let aVal, bVal;
        
        switch (sortBy) {
            case 'name':
                aVal = (a.name || '').toLowerCase();
                bVal = (b.name || '').toLowerCase();
                break;
            case 'startDate':
                aVal = a.startDate || '';
                bVal = b.startDate || '';
                break;
            case 'endDate':
                aVal = a.endDate || '';
                bVal = b.endDate || '';
                break;
            case 'progress':
                const statsA = calculateProjectTaskStats(tasks, a.id);
                const statsB = calculateProjectTaskStats(tasks, b.id);
                aVal = calculateCompletionPercentage(statsA);
                bVal = calculateCompletionPercentage(statsB);
                break;
            case 'taskCount':
                aVal = tasks.filter(t => t.projectId === a.id).length;
                bVal = tasks.filter(t => t.projectId === b.id).length;
                break;
            case 'updated':
                // Use updatedAt if available, fallback to createdAt, then 0 for missing dates
                aVal = Date.parse(a.updatedAt || a.createdAt || '') || 0;
                bVal = Date.parse(b.updatedAt || b.createdAt || '') || 0;
                break;
            default:
                aVal = a.id;
                bVal = b.id;
        }
        
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
    });
}

/**
 * Filter projects by search term
 * @param {Array} projects - Array of project objects
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered projects array
 */
export function filterProjectsBySearch(projects, searchTerm) {
    if (!searchTerm || searchTerm.trim() === '') {
        return projects;
    }
    
    const term = searchTerm.toLowerCase().trim();
    
    return projects.filter(project => {
        const name = (project.name || '').toLowerCase();
        const description = (project.description || '').toLowerCase();
        
        return name.includes(term) || description.includes(term);
    });
}

/**
 * Filter projects by status
 * @param {Array} projects - Array of project objects
 * @param {Set|Array} statusFilter - Set or array of status values to include
 * @param {Array} tasks - Array of all tasks (needed for status calculation)
 * @param {Date} today - Today's date
 * @returns {Array} Filtered projects array
 */
export function filterProjectsByStatus(projects, statusFilter, tasks = [], today = new Date()) {
    if (!statusFilter || (statusFilter.size === 0 && statusFilter.length === 0)) {
        return projects;
    }
    
    const statusSet = statusFilter instanceof Set ? statusFilter : new Set(statusFilter);
    
    return projects.filter(project => {
        const stats = calculateProjectTaskStats(tasks, project.id);
        const statusInfo = determineProjectStatus(project, stats, today);
        return statusSet.has(statusInfo.status);
    });
}

/**
 * Prepare projects view data for rendering
 * @param {Array} projects - Array of project objects
 * @param {Array} tasks - Array of all task objects
 * @param {Object} options - Options
 * @param {string} options.searchTerm - Search term to filter by
 * @param {Set|null} options.statusFilter - Status filter
 * @param {string} options.sortBy - Field to sort by
 * @param {string} options.sortDirection - Sort direction
 * @param {Date} options.today - Today's date
 * @returns {Object} Prepared projects view data
 */
export function prepareProjectsViewData(projects, tasks, options = {}) {
    const {
        searchTerm = '',
        statusFilter = null,
        sortBy = 'name',
        sortDirection = 'asc',
        today = new Date()
    } = options;
    
    // Apply search filter
    let filteredProjects = filterProjectsBySearch(projects, searchTerm);

    // Apply status filter
    if (statusFilter && statusFilter.size > 0) {
        filteredProjects = filterProjectsByStatus(filteredProjects, statusFilter, tasks, today);
    } else {
        // Default behavior: exclude archived projects unless caller explicitly
        // asks for them via statusFilter containing 'archived'.
        filteredProjects = filteredProjects.filter(p => !p.archivedAt);
    }
    
    // Apply sorting
    const sortedProjects = sortProjects(filteredProjects, sortBy, sortDirection, tasks);
    
    // Enhance with stats
    const enhancedProjects = sortedProjects.map(project => {
        const stats = calculateProjectTaskStats(tasks, project.id);
        const statusInfo = determineProjectStatus(project, stats, today);
        const completionPct = calculateCompletionPercentage(stats);
        
        return {
            ...project,
            stats,
            statusInfo,
            completionPct
        };
    });
    
    return {
        projects: enhancedProjects,
        count: enhancedProjects.length,
        totalOriginal: projects.length
    };
}

/**
 * Sort project tasks by priority (desc) and status (asc)
 * @param {Array} tasks - Array of task objects
 * @returns {Array} Sorted tasks
 */
export function sortProjectTasks(tasks) {
    // If any task has a manual projectOrder, use that ordering
    const hasManualOrder = tasks.some(t => typeof t.projectOrder === 'number');
    if (hasManualOrder) {
        return [...tasks].sort((a, b) => {
            const ao = typeof a.projectOrder === 'number' ? a.projectOrder : 9999;
            const bo = typeof b.projectOrder === 'number' ? b.projectOrder : 9999;
            return ao - bo;
        });
    }
    return [...tasks].sort((a, b) => {
        const aPriority = PRIORITY_ORDER[a.priority || 'low'] || 1;
        const bPriority = PRIORITY_ORDER[b.priority || 'low'] || 1;
        if (aPriority !== bPriority) {
            return bPriority - aPriority;
        }
        const aStatus = STATUS_ORDER[a.status || 'todo'] || 4;
        const bStatus = STATUS_ORDER[b.status || 'todo'] || 4;
        return aStatus - bStatus;
    });
}

/**
 * Generate HTML for a single project item (desktop view)
 * @param {Object} project - Project object
 * @param {Array} allTasks - All tasks array
 * @param {Object} helpers - Helper functions and translations
 * @returns {string} HTML string
 */
export function generateProjectItemHTML(project, allTasks, helpers) {
    const {
        escapeHtml,
        renderSingleLinePreviewWithBeautifiedLinks,
        linkPresentationContext,
        formatDatePretty,
        getProjectColor,
        getProjectStatus,
        getProjectStatusLabel,
        getTagColor,
        getTagTextColor,
        getPriorityLabel,
        getStatusLabel,
        getLocale,
        formatDate,
        t
    } = helpers;

    const projectTasks = allTasks.filter(task => task.projectId === project.id && !task.isTemplate && !task.archivedAt);
    const stats = calculateProjectTaskStats(allTasks, project.id);
    const { done: completed, total } = stats;

    const completedPct = total > 0 ? (completed / total) * 100 : 0;
    const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;

    const swatchColor = getProjectColor(project.id);
    const projectStatus = getProjectStatus(project.id);
    const isArchived = Boolean(project.archivedAt);
    const restoreButtonHtml = isArchived
        ? `<button class="pc-btn pc-btn-restore-project" type="button"
                data-action="openProjectRestoreModal" data-param="${project.id}"
                data-stop-propagation="true"
                data-i18n="projects.restore.confirm">
            ${t('projects.restore.confirm')}
        </button>`
        : '';

    const sortedTasks = sortProjectTasks(projectTasks);
    const hasManualOrder = projectTasks.some(t => typeof t.projectOrder === 'number');

    const tasksHtml = sortedTasks.length > 0
        ? sortedTasks.map(task => {
            const priority = task.priority || 'low';
            const dueChipHtml = generateKanbanDueChipHTML(task.endDate, task.status, {
                escapeHtml,
                formatDate: formatDate || ((d) => formatDatePretty(d, getLocale())),
                overdueText: t('tasks.kanban.overdue'),
                startDate: task.startDate
            });
            const tagsHtml = task.tags && task.tags.length > 0
                ? `<div class="task-tags">${task.tags.map(tag => {
                    const bg = getTagColor(tag);
                    return `<span class="pc-task-tag" style="${getTintedTagStyle(bg)}">${escapeHtml(tag.toUpperCase())}</span>`;
                }).join('')}</div>`
                : '';

            return `
                <div class="expanded-task-item${task.status === 'done' ? ' is-done' : ''}" data-action="openTaskDetails" data-param="${task.id}" data-task-id="${task.id}" ${(typeof window !== 'undefined' && window.matchMedia && (window.matchMedia('(max-width: 768px)').matches || window.matchMedia('(pointer: coarse)').matches)) ? '' : 'draggable="true"'} data-stop-propagation="true">
                    <div class="expanded-drag-handle" data-stop-propagation="true">⠿</div>
                    <div class="expanded-task-info">
                        <div class="expanded-task-name">${escapeHtml(task.title || '')}</div>
                        ${tagsHtml}
                    </div>
                    <div class="pc-task-due">${dueChipHtml}</div>
                    <div class="expanded-task-priority">
                        <span class="kanban-priority priority-${priority}"><span class="kanban-priority-dot" aria-hidden="true"></span>${getPriorityLabel(priority)}</span>
                    </div>
                    <div class="expanded-task-status-col">
                        <span class="pc-status-chip" data-status="${task.status}">${getStatusLabel(task.status)}</span>
                    </div>
                </div>
            `;
        }).join('')
        : `<div class="no-tasks-message">${t('tasks.noTasksInProject')}</div>`;

    const initials = escapeHtml(getProjectInitials(project.name));
    // Project tags are tinted with the project's own color (--project-accent, set in CSS)
    const tagsHtml = project.tags && project.tags.length > 0
        ? project.tags.map(tag => `<span class="pc-tag">${escapeHtml(tag.toUpperCase())}</span>`).join('')
        : '';
    const hasDescription = Boolean((project.description || '').trim());
    const pending = total - completed;

    // Date line: "start → end", with "No start"/"No end" for half-set ranges
    const fmt = (d) => escapeHtml(formatDatePretty(d, getLocale()));
    let datesText = '';
    if (project.startDate && project.endDate) datesText = `${fmt(project.startDate)} → ${fmt(project.endDate)}`;
    else if (project.endDate) datesText = `${escapeHtml(t('projects.card.noStart'))} → ${fmt(project.endDate)}`;
    else if (project.startDate) datesText = `${fmt(project.startDate)} → ${escapeHtml(t('projects.card.noEnd'))}`;

    const deadline = getProjectDeadlineInfo(project, projectStatus === 'completed');
    let deadlineChipHtml = '';
    if (deadline) {
        const label = deadline.kind === 'overdue'
            ? t(deadline.days === 1 ? 'projects.card.overdueOne' : 'projects.card.overdueDays', { count: deadline.days })
            : deadline.kind === 'today'
                ? t('projects.card.dueToday')
                : t(deadline.days === 1 ? 'projects.card.dueInOne' : 'projects.card.dueInDays', { count: deadline.days });
        deadlineChipHtml = `<span class="pc-deadline-chip is-${deadline.kind}">${escapeHtml(label)}</span>`;
    }
    const isOverdue = deadline?.kind === 'overdue';

    return `
        <div class="pc-item ${hasDescription ? 'has-description' : 'no-description'}${isOverdue ? ' is-overdue' : ''}" id="project-item-${project.id}" style="--project-accent: ${swatchColor};">

            <!-- ═══ HEADER: monogram + name + status ═══ -->
            <div class="pc-header" data-action="toggleProjectExpand" data-param="${project.id}">
                <div class="pc-header-content">
                    <span class="pc-avatar" aria-hidden="true">${initials}</span>
                    <div class="pc-header-meta">
                        <div class="pc-name"><span class="pc-name-link"
                                  data-action="showProjectDetails"
                                  data-param="${project.id}"
                                  data-stop-propagation="true">${escapeHtml(project.name || t('projects.untitled'))}</span></div>
                        ${tagsHtml ? `<div class="pc-tags-row">${tagsHtml}</div>` : ''}
                    </div>
                    <span class="project-status-badge pc-header-status ${projectStatus}">${getProjectStatusLabel(projectStatus)}</span>
                    <!-- Compact stats for list view only -->
                    <div class="pc-compact-stats">
                        <span class="project-status-badge ${projectStatus}">${getProjectStatusLabel(projectStatus)}</span>
                        <span class="pc-compact-text">${total} ${t('tasks', 'tasks')}</span>
                        <span class="pc-compact-percent">${completionPct}%</span>
                        <div class="pc-compact-progress">
                            <div class="progress-bar-wrapper pc-compact-bar">
                                ${total > 0 ? `
                                    <div class="progress-segment done" style="width: ${(completed/total)*100}%"></div>
                                ` : ''}
                            </div>
                        </div>
                        <button class="pc-btn pc-btn-open pc-btn-project-details" type="button"
                                data-action="showProjectDetails" data-param="${project.id}"
                                data-stop-propagation="true"
                                title="${t('projects.openDetailsTitle')}">
                            ${t('projects.openDetailsTitle')}
                        </button>
                        ${restoreButtonHtml}
                        <svg class="pc-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/></svg>
                    </div>
                </div>
            </div>

            <div class="pc-card-content"><div class="pc-left-col">
            <!-- ═══ CARD BODY: description, then tasks + dates left, ring right ═══ -->
            <div class="pc-body" data-action="toggleProjectExpand" data-param="${project.id}">
                <p class="pc-description">${hasDescription
                    ? renderSingleLinePreviewWithBeautifiedLinks(helpers.stripDescriptionHtml ? helpers.stripDescriptionHtml(project.description || '') : (project.description || ''), linkPresentationContext)
                    : `<span class="pc-description-empty">${escapeHtml(t('projects.noDescription'))}</span>`}</p>

                <div class="pc-stats-row">
                    <div class="pc-stats-detail">
                        <span class="pc-task-meta">${escapeHtml(t('projects.card.tasksProgress', { done: completed, total }))}</span>
                        <div class="pc-dates${datesText ? '' : ' is-empty'}">
                            <svg class="pc-dates-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                            <span class="pc-dates-text">${datesText || escapeHtml(t('tasks.noDatesSet'))}</span>
                            ${deadlineChipHtml}
                        </div>
                    </div>
                    <!-- Progress ring in the project color -->
                    <div class="pc-ring-wrap" role="progressbar" aria-valuenow="${completionPct}" aria-valuemin="0" aria-valuemax="100">
                        <svg class="pc-ring-svg" viewBox="0 0 100 100" aria-hidden="true">
                            <circle class="pc-ring-track" cx="50" cy="50" r="40"/>
                            ${completionPct > 0 ? `<circle class="pc-ring-fill" cx="50" cy="50" r="40"
                                stroke-dasharray="${completionPct * 2.513} ${(100 - completionPct) * 2.513}"
                                style="stroke: ${swatchColor};"/>` : ''}
                        </svg>
                        <div class="pc-ring-label">${completionPct}%</div>
                    </div>
                </div>
            </div>

            <!-- ═══ CARD FOOTER: tags + actions ═══ -->
            <div class="pc-footer">
                <span class="project-status-badge ${projectStatus}">${getProjectStatusLabel(projectStatus)}</span>
                ${!isArchived ? `<button class="pc-project-archive-link" type="button" data-action="openProjectArchiveModal" data-param="${project.id}" data-stop-propagation="true" title="${t('projects.archive.title')}">${t('projects.archive.title')}</button>` : ''}
                <div class="pc-footer-actions">
                    <button class="pc-btn pc-btn-tasks" type="button"
                            data-action="toggleProjectExpand" data-param="${project.id}"
                            data-stop-propagation="true">
                        <span class="pc-btn-tasks-show">${t('projects.card.showTasks')}</span>
                        <span class="pc-btn-tasks-hide">${t('projects.card.hideTasks')}</span>
                        <svg class="pc-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/></svg>
                    </button>
                    <button class="pc-btn pc-btn-open" type="button"
                            data-action="showProjectDetails" data-param="${project.id}"
                            data-stop-propagation="true"
                            title="${t('projects.openDetailsTitle')}">
                        ${t('projects.openDetailsTitle')}
                    </button>
                    ${restoreButtonHtml}
                    ${!isArchived ? `<button class="pc-btn pc-btn-add" type="button"
                            data-action="openTaskModalForProject" data-param="${project.id}"
                            data-stop-propagation="true"
                            title="${t('tasks.addButton')}" aria-label="${t('tasks.addButton')}">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/></svg>
                    </button>` : ''}
                </div>
            </div>

            </div><!-- /pc-left-col -->
            <!-- ═══ EXPANDED TASK PANEL ═══ -->
            <div class="project-tasks-expanded">
                <div class="expanded-tasks-container">
                    <div class="expanded-tasks-header" data-action="toggleProjectExpand" data-param="${project.id}" data-stop-propagation="true">
                        <span class="pc-tasks-title">${t('projects.card.tasksTitle')}</span>
                        <span class="pc-tasks-summary">${escapeHtml(t('projects.card.pendingDone', { pending, done: completed }))}</span>
                        <div class="pc-tasks-actions">
                            ${hasManualOrder ? `<button class="add-btn expanded-add-task-btn auto-sort-btn" type="button" data-action="resetExpandedProjectTaskOrder" data-param="${project.id}" data-stop-propagation="true" title="${t('projects.details.resetSortTitle')}">↕ ${t('projects.details.resetSort')}</button>` : ''}
                            ${total > 0 ? `<button class="add-btn expanded-add-task-btn pc-open-list-btn" type="button" data-action="navigateToProjectTasksList" data-param="${project.id}" data-stop-propagation="true" title="${t('projects.details.viewInList')}">${t('projects.details.viewInListBtn')}</button>` : ''}
                            <button class="add-btn expanded-add-task-btn" type="button" data-action="openTaskModalForProject" data-param="${project.id}" data-stop-propagation="true">${t('tasks.addButton')}</button>
                        </div>
                    </div>
                    ${total > 0 ? `
                        <div class="pc-task-cols" aria-hidden="true" data-action="toggleProjectExpand" data-param="${project.id}" data-stop-propagation="true">
                            <span></span>
                            <span>${t('projects.card.col.task')}</span>
                            <span>${t('projects.card.col.date')}</span>
                            <span>${t('projects.card.col.priority')}</span>
                            <span>${t('projects.card.col.status')}</span>
                        </div>
                    ` : ''}
                    ${tasksHtml}
                </div>
            </div>
            </div><!-- /pc-card-content -->
        </div>
    `;
}

/**
 * Generate HTML for all project items
 * @param {Array} projects - Array of project objects
 * @param {Array} allTasks - All tasks array
 * @param {Object} helpers - Helper functions
 * @returns {string} HTML string for all projects
 */
export function generateProjectsListHTML(projects, allTasks, helpers) {
    return projects.map(project => generateProjectItemHTML(project, allTasks, helpers)).join('');
}
